import type { WorkspaceIntegrationsDto } from '../models/integration.types'
import { useIntegrationStore } from '../stores/integrationStore'

import { registerBootstrapHydrator, type MappedBootstrapResult } from '@/features/account'

function hydrateConnectedIntegrations(result: MappedBootstrapResult): void {
  const store = useIntegrationStore()
  store.selectAccount(result.meta.account_id)
  // `null` means failed/cold: retain warm state for the same account. `[]` is explicit empty.
  if (result.integrationsConnected !== null) {
    store.setConnectedIntegrations(result.integrationsConnected)
  }
}

/**
 * The `integrations` section — all three slices, contrary to contract §7.
 *
 * §7 calls `allowedIntegrations`/`connectableIntegrations` lazy Connections-page fetches. They are
 * not: 07 has mapped all four since `BootstrapMapper.ts:294-297`, and until this registration they
 * were mapped on every boot and dropped on the floor because nothing claimed the section.
 *
 * Being a workspace-level section (07 `flowToolkit.ts:49-55`) this also IS the workspace-switch
 * refresh — `SelectWorkspaceFlow` resets the section and re-dispatches it, so no feature has to call
 * a refresh action the way v3's `workspaceStore.ts:420-421` did.
 */
function hydrateWorkspaceIntegrations(result: MappedBootstrapResult): void {
  const store = useIntegrationStore()
  // Same null convention as above: a failed or cold section must not blank warm state.
  if (result.allIntegrations !== null) {
    // 07 types this slice `unknown[]` — it maps the section without interpreting it. The shape is
    // 12's to declare, and a malformed entry simply fails to match in `integrationsFor`.
    store.setAllIntegrations(result.allIntegrations as WorkspaceIntegrationsDto[])
  }
  if (result.allowedIntegrations !== null) {
    store.setAllowedIntegrations(result.allowedIntegrations, result.integrationGroups)
  }
  if (result.connectableIntegrations !== null) {
    store.setConnectableIntegrations(result.connectableIntegrations)
  }
}

let unregister: (() => void) | null = null

/** Register module 12 as the owner of both bootstrap integration sections. */
export function registerIntegrationBootstrapHydrators(): () => void {
  unregister?.()
  const store = useIntegrationStore()
  const disposers = [
    registerBootstrapHydrator('integrations_connected', {
      hydrate: hydrateConnectedIntegrations,
      reset: () => store.reset(),
    }),
    registerBootstrapHydrator('integrations', {
      hydrate: hydrateWorkspaceIntegrations,
      reset: () => store.reset(),
    }),
  ]
  unregister = () => {
    for (const dispose of disposers) dispose()
    unregister = null
  }
  return unregister
}
